import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Automatically walks child nodes in a printable workspace container and inserts spacers
 * before elements that cross the page boundary, preventing them from being sliced in half.
 */
export const applyPageBreaks = (container: HTMLElement): void => {
  // Clear any existing spacers from previous runs
  const existingSpacers = container.querySelectorAll('.pdf-page-spacer');
  existingSpacers.forEach(s => s.remove());

  // A4 dimensions at standard 96 DPI: 794px width x 1123px height
  const width = container.clientWidth || 794;
  const pageHeightPx = Math.floor(width * 1.414);

  // Select key blocks we want to keep intact (avoid splitting inside them)
  const query = '.document-html-content > *, .document-html-content li, .document-html-content tr, .document-html-content blockquote, .document-html-content img, .document-html-content svg';
  const elements = container.querySelectorAll(query);

  const containerTop = container.getBoundingClientRect().top;

  for (let i = 0; i < elements.length; i++) {
    const element = elements[i] as HTMLElement;

    // Skip if element has no height
    if (element.offsetHeight === 0) continue;

    const rect = element.getBoundingClientRect();
    const elemTop = rect.top - containerTop;
    const elemBottom = elemTop + element.offsetHeight;

    const pageIndex = Math.floor(elemTop / pageHeightPx);
    const pageEndIndex = Math.floor(elemBottom / pageHeightPx);

    // If element spans across a page boundary (starts on one page, ends on the next)
    if (pageEndIndex > pageIndex) {
      // Only insert spacer if the element itself can fit entirely on a single page
      if (element.offsetHeight < pageHeightPx) {
        const spacerHeight = ((pageIndex + 1) * pageHeightPx) - elemTop;

        const tagName = element.tagName.toLowerCase();
        if (tagName === 'tr') {
          // Table rows must use a row spacer inside tbody
          const spacerRow = document.createElement('tr');
          spacerRow.className = 'pdf-page-spacer';
          const spacerCell = document.createElement('td');
          const table = element.closest('table');
          const colCount = table ? (table.querySelector('tr')?.cells.length || 1) : 1;
          spacerCell.setAttribute('colspan', colCount.toString());
          spacerCell.setAttribute('style', `height: ${spacerHeight}px; padding: 0; border: none; background: transparent;`);
          spacerRow.appendChild(spacerCell);
          element.parentNode?.insertBefore(spacerRow, element);
        } else if (tagName === 'li') {
          // List items must use an empty li spacer inside ul/ol
          const spacerLi = document.createElement('li');
          spacerLi.className = 'pdf-page-spacer';
          spacerLi.setAttribute('style', `height: ${spacerHeight}px; list-style-type: none; border: none; background: transparent; margin: 0; padding: 0;`);
          element.parentNode?.insertBefore(spacerLi, element);
        } else {
          // Standard block element spacer div
          const spacer = document.createElement('div');
          spacer.className = 'pdf-page-spacer';
          spacer.setAttribute('style', `height: ${spacerHeight}px; width: 100%; clear: both; display: block; border: none; background: transparent; margin: 0; padding: 0;`);
          element.parentNode?.insertBefore(spacer, element);
        }
      }
    }
  }
};

/**
 * Generates and downloads a high-fidelity PDF from an HTML element using html2canvas & jsPDF.
 * This supports full styling, custom colors, diagrams, shapes, and images securely in the browser.
 */
export const exportHTMLToPDF = async (element: HTMLElement, filename: string): Promise<void> => {
  // Save original style values to restore later
  const originalHeight = element.style.height;
  const originalMaxHeight = element.style.maxHeight;
  const originalOverflowY = element.style.overflowY;

  try {
    // Force element to expand to full height for complete capture
    element.style.height = 'auto';
    element.style.maxHeight = 'none';
    element.style.overflowY = 'visible';

    // Apply page break adjustments
    applyPageBreaks(element);

    const canvas = await html2canvas(element, {
      scale: 2, // High resolution
      useCORS: true, // Allow cross-origin images (e.g. Unsplash)
      allowTaint: false,
      logging: false,
      backgroundColor: '#ffffff',
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.98);
    const pdf = new jsPDF('p', 'mm', 'a4');
    
    const pageWidth = 210; // A4 dimensions in mm
    const pageHeight = 297;
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    let heightLeft = imgHeight;
    let position = 0;

    // First page
    pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    // Add subsequent pages if content overflows A4 height
    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save(filename);
  } catch (error) {
    console.error('HTML to PDF export failed:', error);
    throw error;
  } finally {
    // Restore original styles
    element.style.height = originalHeight;
    element.style.maxHeight = originalMaxHeight;
    element.style.overflowY = originalOverflowY;

    // Remove any spacers inserted
    const spacers = element.querySelectorAll('.pdf-page-spacer');
    spacers.forEach(s => s.remove());
  }
};

/**
 * Downloads a spreadsheet file (Excel/Google Sheets compatible) as a UTF-8 CSV with Byte Order Mark (BOM).
 */
export const exportToCSV = (filename: string, content: string): void => {
  // Ensure the filename has a .csv extension
  const safeFilename = filename.toLowerCase().endsWith('.csv') || filename.toLowerCase().endsWith('.xls') || filename.toLowerCase().endsWith('.xlsx')
    ? filename
    : `${filename}.csv`;

  // Add UTF-8 BOM so Excel opens it with correct character encoding and column mapping
  const blob = new Blob(['\uFEFF' + content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', safeFilename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Downloads a plain text or markdown document.
 */
export const exportToText = (filename: string, content: string): void => {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
